import { Prefab } from '@gift/basic-scene-core/Prefab';
import { createSelector } from '@reduxjs/toolkit';
import { selectTenantId } from '@gift/website-state/slices/tenantSlice';
import { selectActiveVariantId, selectVariantViewbox } from '@gift/website-state/slices/variantsSlice';
import { Translate } from '@gift/types/generic/Translate';
import { AppThunk, WebsiteState } from '@gift/website-state/store';
import { mergeInputsInto } from '@gift/basic-scene-website/preview/helpers/mergeInputsIntoPrefab';
import { unwrap } from '@gift/common/helpers/unwrap';
import { actions as variableActions, selectPinnedVariables } from '@gift/website-state/slices/variablesSlice';
import { batch } from 'react-redux';
import { actions as inputActions } from '@gift/website-state/slices/inputsSlice';
import { TransformParameters } from '@gift/common/helpers/transformParameters';
import { selectSceneHeight, selectSceneWidth } from '@gift/basic-scene-website/_redux-scene';
import { selectActivePrefabId, updateCommission, updatePrefabId } from '@gift/basic-scene-website/_redux-meta';
import { selectResources } from '@gift/website-state/slices/resourcesSlice';

export const selectPrefabs = selectResources<Prefab>('prefabs');

const filterPrefabs = (
    prefabs: Prefab[],
    tenantId: string,
    activePrefabId: string,
    variantId: string
) => {
    return prefabs
        .filter(p => !p.excludedFromVariants?.includes(variantId) || p.id === activePrefabId)
        .filter(p => !p.exclusiveTo?.length || p.exclusiveTo.includes(tenantId));
};

export const selectFilteredPrefabs = createSelector([
    selectPrefabs,
    selectTenantId,
    selectActivePrefabId,
    selectActiveVariantId
], filterPrefabs);

export const applyPrefab = (prefab: Prefab, t: Translate): AppThunk => {

    return (dispatch, getState) => {

        const state = getState();
        const mergedInputs = mergeInputsInto(prefab, state.inputs, t, { direction: 'prefabIntoInput' });
        const newVariables = unwrap(prefab.variables);
        const allVariables = newVariables.concat(selectPinnedVariables(state));

        // Set inputs BEFORE variables: we always do `variables.map(v => inputs[v.id])`,
        // so adding a variable before its corresponding input may cause errors.
        //
        // (The `batch(...)` wrapper ensures React components aren't notified until both
        // dispatches finish, but it does not apply to store subscribers.)

        batch(() => {
            dispatch(inputActions.upsertMany(unwrap(mergedInputs)));
            dispatch(variableActions.setAll(allVariables));
            dispatch(updateCommission(prefab.commission));
            dispatch(updatePrefabId(prefab.id));
        });
    };
};

export const selectPrefabById = createSelector([
    selectPrefabs,
    (state: WebsiteState, id: string) => id
], (a, prefabId) => a.find(prefab => prefab.id === prefabId)!);

export const selectPrefabViewboxById = createSelector([
    (state: WebsiteState, id: string) => selectPrefabById(state, id)
], prefab => prefab.viewbox);

export const selectTransformParametersForPreviewByPrefabId = createSelector([
    selectSceneWidth,
    selectSceneHeight,
    selectVariantViewbox,
    selectPrefabViewboxById
], (sceneWidth, sceneHeight, variantViewbox, prefabViewbox): TransformParameters => ({
    sceneWidth,
    sceneHeight,
    variantViewboxX: variantViewbox.x,
    variantViewboxY: variantViewbox.y,
    variantViewboxWidth: variantViewbox.width,
    variantViewboxHeight: variantViewbox.height,
    designViewboxWidth: prefabViewbox.width,
    designViewboxHeight: prefabViewbox.height
}));

export const selectTransformParametersForMinimalPrefabPreview = createSelector([
    (state: WebsiteState, prefabId: string) => selectPrefabViewboxById(state, prefabId)
], (prefabViewbox): TransformParameters => ({

    sceneWidth: prefabViewbox.width,
    sceneHeight: prefabViewbox.height,

    variantViewboxX: 0,
    variantViewboxY: 0,

    variantViewboxWidth: prefabViewbox.width,
    variantViewboxHeight: prefabViewbox.height,

    designViewboxWidth: prefabViewbox.width,
    designViewboxHeight: prefabViewbox.height
}));

export const selectTransformParametersForPreview = createSelector([
    state => state,
    selectActivePrefabId
], selectTransformParametersForPreviewByPrefabId);
